<template>
  <div>
    <authorisation-wrapper
      :resource="EMAIL_SECURITY"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-dropdown
        :dropdown-text="$t('dropdown')"
        test-id="incident-actions-dropdown"
        :title="$t('actions')"
        :readonly="!isWriteAllowed"
      >
        <cl-dropdown-item
          v-if="isRemediationVisible"
          data-test-id="incident-actions-dropdown-sent_junk-btn"
          @on-click="applyIncidentStatus(IncidentAction.JUNK)"
        >
          <action-dropdown-item
            :title="$t('remediate')"
            :hint="$t('actions-remediate')"
          />
        </cl-dropdown-item>
        <cl-dropdown-item
          v-if="isMarkAsSafeVisible"
          data-test-id="incident-actions-dropdown-mark_safe-btn"
          @on-click="applyIncidentStatus(IncidentAction.SAFE)"
        >
          <action-dropdown-item
            :title="$t('mark-as-safe')"
            :hint="$t('actions-mark-as-safe')"
          />
        </cl-dropdown-item>
        <cl-dropdown-item
          data-test-id="incident-actions-dropdown-exempt-email-button"
          @on-click="onExemptActionClick('email')"
        >
          <action-dropdown-item
            :title="$t('exempt-sender')"
            :hint="$t('description-exempt-sender')"
          />
        </cl-dropdown-item>
        <cl-dropdown-item
          data-test-id="incident-actions-dropdown-exempt-domain-button"
          @on-click="onExemptActionClick('domain')"
        >
          <action-dropdown-item
            :title="$t('exempt-domain')"
            :hint="$t('description-exempt-domain')"
          />
        </cl-dropdown-item>
      </cl-dropdown>
    </authorisation-wrapper>
    <exemption-add
      v-if="isExemptState(state)"
      :default-type="state.senderType"
      :default-value="state.senderValue"
      :disabled="false"
      :auto-open="true"
      @close="setState({ type: READY })"
      @success="setState({ type: READY })"
    />
  </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/store/authentication.store";

import ExemptionAdd from "@/modules/PhishTitan/views/Common/_shared/Exemptions/exemption-add.vue";
import ActionDropdownItem from "../../_shared/action-dropdown-item.vue";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import { computed } from "vue";
import {
  EXEMPT,
  READY,
  useIncidentsState,
} from "@/modules/PhishTitan/composables/useIncidentsState";
import { useI18n } from "vue-i18n";
import { trackEvent } from "@/utils/track-event";
import { AuthorisationWrapper, EMAIL_SECURITY, WRITE } from "@/authorisation";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { MSP, CUSTOMER } from "@/constants";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { isRemediable, isMarkableAsSafe } from "./IncidentsList.utils";
import { IncidentAction } from "./IncidentList.types";
import { ExemptionsType } from "../../_shared/Exemptions/exemptions.types";
import { useIncidents } from "../useIncidents.store";

const emit = defineEmits<{
  success: [];
}>();

const toastStore = useToastStore();

const { isMspRoute } = useIsMspRoute();

const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const { state, setState, isExemptState } = useIncidentsState();

const authStore = useAuthenticationStore();
const { t } = useI18n();
const { updateIncidentStatus, incidentDetails: incident } = useIncidents();

const internetMessageID = computed(() => incident.value?.internet_message_id);
const mailSpMessageID = computed(() => incident.value?.mail_sp_message_id);

const applyIncidentStatus = async (status: IncidentAction) => {
  if (!globalOrganisationId.value) return;

  const isStatusJunk = status === IncidentAction.JUNK;

  trackEvent({
    category: "Incident Actions Dropdown",
    action: "action selected",
    name: isStatusJunk ? "remediate" : "mark as save",
  });

  if (!(
    globalOrganisationId.value &&
    internetMessageID.value &&
    mailSpMessageID.value
  )) {
    toastStore.displayToast({
      title: t("error"),
      message: t("incident-status-update"),
      duration: 2000,
      variant: "danger",
      testId: "incident-actions-dropdown-toast-error",
    });
  }

  try {
    const response = await updateIncidentStatus({
      globalOrganisationID: globalOrganisationId.value!,
      ingressID: incident.value?.ingress_id!,
      status,
      params: {
        organisationType: isMspRoute.value ? MSP : CUSTOMER,
      },
    });

    emit("success");

    const isAsync = response?.status === 202;

    const successMessage = isStatusJunk
      ? t("remediate-success")
      : t("mark-as-safe-success");

    if (isAsync) {
      toastStore.displayToast({
        title: t("error"),
        message: t("action-in-progress"),
        duration: 3000,
        variant: "danger",
        testId: "incident-actions-dropdown-toast-in-progress",
      });
    } else {
      toastStore.displayToast({
        title: t("success"),
        message: successMessage,
        duration: 2000,
        variant: "success",
        testId: "incident-actions-dropdown-toast-success",
      });
    }
  } catch {
    // Handled in axios interceptor
  }
};

const onExemptActionClick = async (senderType: ExemptionsType) => {
  const isSenderTypeEmail = senderType === "email";

  trackEvent({
    category: "Incident Actions Dropdown",
    action: "action selected",
    name: isSenderTypeEmail ? "exempt sender" : "exempt domain",
  });

  await authStore.setCustomer({
    uuid: incident?.value?.customer_global_accountid,
  });

  const senderMail = incident?.value?.message?.sender_mail;
  if (!senderMail) {
    return;
  }

  const senderValue = isSenderTypeEmail ? senderMail : senderMail.split("@")[1];

  setState({
    type: EXEMPT,
    senderValue,
    senderType,
  });
};

const uiStatus = computed(() => incident.value?.ui_status);

const isRemediationVisible = computed(() => {
  if (!uiStatus.value) {
    return false;
  }
  return isRemediable(uiStatus.value);
});
const isMarkAsSafeVisible = computed(() => {
  if (!uiStatus.value) {
    return false;
  }
  return isMarkableAsSafe(uiStatus.value);
});
</script>
